import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import type React from 'react'
import { api } from '../services/fetch'
import { notificationService } from '../services/notificationService'
import type { Project } from '../types/project'
import { StakeholderType, UserType } from '../types/user'
import type { WorkOrder } from '../types/workorder'

interface TagNotifications {
  unreadCount: number
  markAsRead: (tagId: number) => Promise<void>
  markAllAsRead: () => Promise<void>
}

interface ProjectNotifications {
  unreadCount: number
  markAsRead: (projectId: number) => Promise<void>
  markAllAsRead: () => Promise<void>
  markAcceptedAsRead: () => Promise<void>
}

interface WorkOrderNotifications {
  unreadCount: number // Legacy: total count (for backward compatibility)
  pendingCount: number // New: pending work orders count
  acceptedCount: number // New: accepted work orders count
  markAsRead: (workOrderId: number) => Promise<void>
  markAllAsRead: () => Promise<void>
  markAcceptedAsRead: () => Promise<void>
  // New split methods
  markAllPendingAsRead: () => Promise<void>
  markAllAcceptedAsRead: () => Promise<void>
}

interface InvoiceNotifications {
  unreadCount: number
  markAsRead: (invoiceId: number) => Promise<void>
  markAllAsRead: () => Promise<void>
}

interface NotificationContextType {
  tags: TagNotifications
  projects: ProjectNotifications
  workOrders: WorkOrderNotifications
  invoices: InvoiceNotifications
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
)

interface NotificationProviderProps {
  children: React.ReactNode
  currentProfile?: StakeholderType | string
  user?: {
    user_type?: string
    stakeholder_types?: string[]
  } | null
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  currentProfile,
  user,
}) => {
  // Notification counts state
  const [unreadTagsCount, setUnreadTagsCount] = useState<number>(0)
  const [unreadProjectsCount, setUnreadProjectsCount] = useState<number>(0)
  const [unreadWorkOrdersCount, setUnreadWorkOrdersCount] = useState<number>(0)
  // New split work order counts
  const [unreadPendingWorkOrdersCount, setUnreadPendingWorkOrdersCount] =
    useState<number>(0)
  const [unreadAcceptedWorkOrdersCount, setUnreadAcceptedWorkOrdersCount] =
    useState<number>(0)
  const [unreadInvoicesCount, setUnreadInvoicesCount] = useState<number>(0)

  // Shared state
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Determine user roles
  const profileType = currentProfile as StakeholderType
  const isDriver = user?.user_type === UserType.DRIVER // ✅ NEW: Check user_type instead of stakeholder_type
  const isBroker = profileType === StakeholderType.BROKER
  const isContractor = profileType === StakeholderType.CONTRACTOR
  const isSubhauler = profileType === StakeholderType.SUB_HAULER
  const canAccessProjects = isBroker || isContractor || isSubhauler

  // Refresh all notification counts
  const refresh = useCallback(async () => {
    if (!currentProfile) return

    setIsLoading(true)
    try {
      const promises: Promise<unknown>[] = []

      // Fetch tag notifications for drivers
      if (isDriver) {
        promises.push(notificationService.getUnreadTagsCount())
      }

      // Fetch project and work order notifications for brokers/contractors/subhaulers
      if (canAccessProjects) {
        promises.push(
          notificationService.getUnreadProjectsCount(),
          notificationService.getUnreadWorkOrdersCount(), // Legacy total count
          notificationService.getUnreadPendingWorkOrdersCount(), // New pending count
          notificationService.getUnreadAcceptedWorkOrdersCount(), // New accepted count
        )
      }

      // Fetch invoice notifications for all users
      promises.push(notificationService.getUnreadInvoicesCount())

      const results = await Promise.all(promises)
      let resultIndex = 0

      if (isDriver) {
        setUnreadTagsCount(
          (results[resultIndex] as { unread_count: number }).unread_count,
        )
        resultIndex += 1
      }

      if (canAccessProjects) {
        setUnreadProjectsCount(
          (results[resultIndex] as { unread_count: number }).unread_count,
        )
        setUnreadWorkOrdersCount(
          (results[resultIndex + 1] as { unread_count: number }).unread_count,
        )
        setUnreadPendingWorkOrdersCount(
          (results[resultIndex + 2] as { unread_count: number }).unread_count,
        )
        setUnreadAcceptedWorkOrdersCount(
          (results[resultIndex + 3] as { unread_count: number }).unread_count,
        )
        resultIndex += 4
      }

      // Invoice notifications (always fetched)
      setUnreadInvoicesCount(
        (results[resultIndex] as { unread_count: number }).unread_count,
      )

      setError(null)
    } catch (err) {
      console.error('Failed to fetch notification counts:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch notification counts'),
      )
    } finally {
      setIsLoading(false)
    }
  }, [isDriver, canAccessProjects, currentProfile])

  // Tag notification methods
  const markTagAsRead = useCallback(
    async (tagId: number) => {
      if (!isDriver) {
        return
      }

      try {
        await notificationService.markTagAsRead(tagId)

        // Optimistic update - immediately decrease count
        setUnreadTagsCount((prev) => Math.max(0, prev - 1))
        setError(null)
      } catch (err) {
        console.error('Failed to mark tag as read:', err)
        // Refresh to get accurate count on error
        await refresh()
        setError(
          err instanceof Error ? err : new Error('Failed to mark tag as read'),
        )
      }
    },
    [isDriver, refresh],
  )

  const markAllTagsAsRead = useCallback(async () => {
    if (!isDriver || unreadTagsCount === 0) return

    // Optimistic update
    const previousTagTotal = unreadTagsCount
    setUnreadTagsCount(0)

    try {
      await notificationService.markAllTagsAsRead()
      setError(null)
    } catch (err) {
      // Revert optimistic update on error
      setUnreadTagsCount(previousTagTotal)

      console.error('Failed to mark all tags as read:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark all tags as read'),
      )
    }
  }, [isDriver, unreadTagsCount])

  // Project notification methods
  const markProjectAsRead = useCallback(
    async (projectId: number) => {
      if (!canAccessProjects) return

      try {
        await notificationService.markProjectAsRead(projectId)
        // Optimistic update - immediately decrease count
        setUnreadProjectsCount((prev) => Math.max(0, prev - 1))
        setError(null)
      } catch (err) {
        console.error('Failed to mark project as read:', err)
        // Refresh to get accurate count on error
        await refresh()
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to mark project as read'),
        )
      }
    },
    [canAccessProjects, refresh],
  )

  const markAllProjectsAsRead = useCallback(async () => {
    if (!canAccessProjects || unreadProjectsCount === 0) return

    // Optimistic update
    const previousProjectTotal = unreadProjectsCount
    setUnreadProjectsCount(0)

    try {
      await notificationService.markAllProjectsAsRead()
      setError(null)
    } catch (err) {
      // Revert optimistic update on error
      setUnreadProjectsCount(previousProjectTotal)

      console.error('Failed to mark all projects as read:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark all projects as read'),
      )
    }
  }, [canAccessProjects, unreadProjectsCount])

  const markAcceptedProjectsAsRead = useCallback(async () => {
    if (!canAccessProjects) return

    try {
      // Get the actual unread projects to mark them individually
      const response = await api.get('/notifications/unread-projects/')
      const unreadProjects: Project[] = response.data

      // Filter for accepted projects and mark each as read
      const acceptedProjects = unreadProjects.filter(
        (project: Project) => project.status?.toUpperCase() === 'ACCEPTED',
      )

      if (acceptedProjects.length > 0) {
        // Optimistic update - reduce count by number of accepted projects
        setUnreadProjectsCount((prev) =>
          Math.max(0, prev - acceptedProjects.length),
        )

        // Mark each accepted project as read
        await Promise.all(
          acceptedProjects.map((project: Project) =>
            notificationService.markProjectAsRead(project.id),
          ),
        )
      }

      setError(null)
    } catch (err) {
      console.error('Failed to mark accepted projects as read:', err)
      // Refresh to get accurate count on error
      await refresh()
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark accepted projects as read'),
      )
    }
  }, [canAccessProjects, refresh])

  // Work order notification methods
  const markWorkOrderAsRead = useCallback(
    async (workOrderId: number) => {
      if (!canAccessProjects) return

      try {
        await notificationService.markWorkOrderAsRead(workOrderId)
        // Optimistic update - immediately decrease count
        setUnreadWorkOrdersCount((prev) => Math.max(0, prev - 1))
        setError(null)
      } catch (err) {
        console.error('Failed to mark work order as read:', err)
        // Refresh to get accurate count on error
        await refresh()
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to mark work order as read'),
        )
      }
    },
    [canAccessProjects, refresh],
  )

  const markAllWorkOrdersAsRead = useCallback(async () => {
    if (!canAccessProjects || unreadWorkOrdersCount === 0) return

    // Optimistic update
    const previousWorkOrderTotal = unreadWorkOrdersCount
    setUnreadWorkOrdersCount(0)

    try {
      await notificationService.markAllWorkOrdersAsRead()
      setError(null)
    } catch (err) {
      // Revert optimistic update on error
      setUnreadWorkOrdersCount(previousWorkOrderTotal)

      console.error('Failed to mark all work orders as read:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark all work orders as read'),
      )
    }
  }, [canAccessProjects, unreadWorkOrdersCount])

  const markAcceptedWorkOrdersAsRead = useCallback(async () => {
    if (!canAccessProjects) return

    try {
      // Get the actual unread work orders to mark them individually
      const response = await api.get('/notifications/unread-workorders/')
      const unreadWorkOrders: WorkOrder[] = response.data

      // Filter for accepted work orders and mark each as read
      const acceptedWorkOrders = unreadWorkOrders.filter(
        (workOrder: WorkOrder) =>
          workOrder.status?.toUpperCase() === 'ACCEPTED',
      )

      if (acceptedWorkOrders.length > 0) {
        // Optimistic update - reduce count by number of accepted work orders
        setUnreadWorkOrdersCount((prev) =>
          Math.max(0, prev - acceptedWorkOrders.length),
        )

        // Mark each accepted work order as read
        await Promise.all(
          acceptedWorkOrders.map((workOrder: WorkOrder) =>
            workOrder.id
              ? notificationService.markWorkOrderAsRead(Number(workOrder.id))
              : Promise.resolve(),
          ),
        )
      }

      setError(null)
    } catch (err) {
      console.error('Failed to mark accepted work orders as read:', err)
      // Refresh to get accurate count on error
      await refresh()
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark accepted work orders as read'),
      )
    }
  }, [canAccessProjects, refresh])

  // New split work order notification methods
  const markAllPendingWorkOrdersAsRead = useCallback(async () => {
    if (!canAccessProjects || unreadPendingWorkOrdersCount === 0) return

    // Optimistic update
    const previousPendingCount = unreadPendingWorkOrdersCount
    setUnreadPendingWorkOrdersCount(0)
    // Also update total count
    setUnreadWorkOrdersCount((prev) => Math.max(0, prev - previousPendingCount))

    try {
      await notificationService.markAllPendingWorkOrdersAsRead()
      setError(null)
    } catch (err) {
      // Revert optimistic update on error
      setUnreadPendingWorkOrdersCount(previousPendingCount)
      setUnreadWorkOrdersCount((prev) => prev + previousPendingCount)

      console.error('Failed to mark all pending work orders as read:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark all pending work orders as read'),
      )
    }
  }, [canAccessProjects, unreadPendingWorkOrdersCount])

  const markAllAcceptedWorkOrdersAsRead = useCallback(async () => {
    if (!canAccessProjects || unreadAcceptedWorkOrdersCount === 0) return

    // Optimistic update
    const previousAcceptedCount = unreadAcceptedWorkOrdersCount
    setUnreadAcceptedWorkOrdersCount(0)
    // Also update total count
    setUnreadWorkOrdersCount((prev) =>
      Math.max(0, prev - previousAcceptedCount),
    )

    try {
      await notificationService.markAllAcceptedWorkOrdersAsRead()
      setError(null)
    } catch (err) {
      // Revert optimistic update on error
      setUnreadAcceptedWorkOrdersCount(previousAcceptedCount)
      setUnreadWorkOrdersCount((prev) => prev + previousAcceptedCount)

      console.error('Failed to mark all accepted work orders as read:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark all accepted work orders as read'),
      )
    }
  }, [canAccessProjects, unreadAcceptedWorkOrdersCount])

  // Invoice notification methods
  const markInvoiceAsRead = useCallback(
    async (invoiceId: number) => {
      try {
        await notificationService.markInvoiceAsRead(invoiceId)
        setUnreadInvoicesCount((prev) => Math.max(0, prev - 1))
        setError(null)
      } catch (err) {
        console.error('Failed to mark invoice as read:', err)
        await refresh()
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to mark invoice as read'),
        )
      }
    },
    [refresh],
  )

  const markAllInvoicesAsRead = useCallback(async () => {
    if (unreadInvoicesCount === 0) return

    const previousCount = unreadInvoicesCount
    setUnreadInvoicesCount(0)

    try {
      await notificationService.markAllInvoicesAsRead()
      setError(null)
    } catch (err) {
      setUnreadInvoicesCount(previousCount)
      console.error('Failed to mark all invoices as read:', err)
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to mark all invoices as read'),
      )
    }
  }, [unreadInvoicesCount])

  // Auto-fetch on mount and set up polling
  useEffect(() => {
    if (currentProfile && (isDriver || canAccessProjects)) {
      refresh()

      // Poll every 30 seconds, but only while the tab is visible — hidden
      // tabs skip polling and refresh immediately when they become visible
      const interval = setInterval(() => {
        if (!document.hidden) {
          refresh()
        }
      }, 30000)

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          refresh()
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        clearInterval(interval)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [isDriver, canAccessProjects, currentProfile, refresh])

  const value: NotificationContextType = {
    tags: {
      unreadCount: unreadTagsCount,
      markAsRead: markTagAsRead,
      markAllAsRead: markAllTagsAsRead,
    },
    projects: {
      unreadCount: unreadProjectsCount,
      markAsRead: markProjectAsRead,
      markAllAsRead: markAllProjectsAsRead,
      markAcceptedAsRead: markAcceptedProjectsAsRead,
    },
    workOrders: {
      unreadCount: unreadWorkOrdersCount,
      pendingCount: unreadPendingWorkOrdersCount,
      acceptedCount: unreadAcceptedWorkOrdersCount,
      markAsRead: markWorkOrderAsRead,
      markAllAsRead: markAllWorkOrdersAsRead,
      markAcceptedAsRead: markAcceptedWorkOrdersAsRead,
      markAllPendingAsRead: markAllPendingWorkOrdersAsRead,
      markAllAcceptedAsRead: markAllAcceptedWorkOrdersAsRead,
    },
    invoices: {
      unreadCount: unreadInvoicesCount,
      markAsRead: markInvoiceAsRead,
      markAllAsRead: markAllInvoicesAsRead,
    },
    isLoading,
    error,
    refresh,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotificationContext = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider',
    )
  }
  return context
}
